/**
 * HardwareForm component - Specialized form for adding/editing hardware products
 * Handles screws, nails, lipping, fasteners, moulding with their specific rate structures
 */
import React, { useState, useEffect } from 'react';
import { api } from '../../lib/store';
import { logger } from '../../lib/logger';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Save } from 'lucide-react';

import { HARDWARE_TYPES, HARDWARE_SUBCATEGORIES } from './constants';

const emptyVariant = {
  size: '',
  nail_number: '',
  box_qty: '',
  rate_box: '',
  rate_dozen: '',
  rate_piece: '',
  rate_kg: '',
  rate_running_feet: '',
};

export function HardwareForm({ open, onOpenChange, editProduct, onSaved }) {
  const [form, setForm] = useState({
    brand_name: '',
    subcategory: '',
    hardware_type: 'screw',
    hardware_variants: [{ ...emptyVariant }],
    show_brand_in_pdf: true,
  });
  const [saving, setSaving] = useState(false);
  const [allBrands, setAllBrands] = useState([]);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [productTypeSuggestions, setProductTypeSuggestions] = useState([]);
  const [showProductTypeDropdown, setShowProductTypeDropdown] = useState(false);

  // Load brands
  useEffect(() => {
    api.getBrands().then(res => setAllBrands(res.data || [])).catch((err) => logger.warn('Failed to load brands:', err));
  }, []);

  // Load edit product data
  useEffect(() => {
    if (editProduct) {
      setForm({
        brand_name: editProduct.brand_name || '',
        subcategory: editProduct.subcategory || '',
        hardware_type: editProduct.hardware_type || 'screw',
        hardware_variants: editProduct.hardware_variants?.length > 0 
          ? editProduct.hardware_variants 
          : [{ ...emptyVariant }],
        show_brand_in_pdf: editProduct.show_brand_in_pdf !== false,
      });
    } else {
      setForm({
        brand_name: '',
        subcategory: '',
        hardware_type: 'screw',
        hardware_variants: [{ ...emptyVariant }],
        show_brand_in_pdf: true,
      });
    }
  }, [editProduct, open]);

  const handleBrandInput = (value) => {
    setForm(f => ({ ...f, brand_name: value }));
    if (value.length > 0) {
      const filtered = allBrands.filter(b => b.toLowerCase().includes(value.toLowerCase())).slice(0, 6);
      setBrandSuggestions(filtered);
      setShowBrandDropdown(filtered.length > 0);
    } else {
      setShowBrandDropdown(false);
    }
  };

  const selectBrand = (brand) => {
    setForm(f => ({ ...f, brand_name: brand }));
    setShowBrandDropdown(false);
  };

  const handleProductTypeInput = (value) => {
    setForm(f => ({ ...f, subcategory: value }));
    if (value.length > 0) {
      const options = getSubcategoryOptions();
      const filtered = options.filter(s => s.toLowerCase().includes(value.toLowerCase())).slice(0, 6);
      setProductTypeSuggestions(filtered);
      setShowProductTypeDropdown(filtered.length > 0);
    } else {
      // Show all options when empty and focused
      setProductTypeSuggestions(getSubcategoryOptions());
      setShowProductTypeDropdown(true);
    }
  };

  const selectProductType = (type) => {
    setForm(f => ({ ...f, subcategory: type }));
    setShowProductTypeDropdown(false);
  };

  const handleTypeChange = (type) => {
    setForm(f => ({ 
      ...f, 
      hardware_type: type,
      subcategory: '',
      hardware_variants: [{ ...emptyVariant }]
    }));
  };

  const addVariant = () => {
    setForm(f => ({
      ...f,
      hardware_variants: [...f.hardware_variants, { ...emptyVariant }]
    }));
  };

  const removeVariant = (index) => {
    setForm(f => ({
      ...f,
      hardware_variants: f.hardware_variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariant = (index, field, value) => {
    setForm(f => ({
      ...f,
      hardware_variants: f.hardware_variants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const getCurrentTypeConfig = () => {
    return HARDWARE_TYPES.find(t => t.id === form.hardware_type) || HARDWARE_TYPES[0];
  };

  const getSubcategoryOptions = () => {
    return HARDWARE_SUBCATEGORIES[form.hardware_type] || [];
  };

  const handleSave = async () => {
    if (form.hardware_type !== 'lipping' && !form.brand_name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    if (!form.subcategory) {
      toast.error('Please select a subcategory/type');
      return;
    }
    
    // Validate variants
    const validVariants = form.hardware_variants.filter(v => v.size.trim());
    if (validVariants.length === 0) {
      toast.error('Please add at least one size/variant');
      return;
    }

    setSaving(true);
    try {
      const typeConfig = getCurrentTypeConfig();
      
      // Clean up variants - only include relevant rate fields
      const cleanedVariants = validVariants.map(v => {
        const cleaned = { size: v.size.trim() };
        // Include nail_number for nails
        if (form.hardware_type === 'nail' && v.nail_number) cleaned.nail_number = v.nail_number.trim();
        if (typeConfig.hasBoxQty && v.box_qty) cleaned.box_qty = parseInt(v.box_qty) || 0;
        if (typeConfig.rateOptions.includes('per box') && v.rate_box) cleaned.rate_box = parseFloat(v.rate_box) || 0;
        if (typeConfig.rateOptions.includes('per dozen') && v.rate_dozen) cleaned.rate_dozen = parseFloat(v.rate_dozen) || 0;
        if (typeConfig.rateOptions.includes('per piece') && v.rate_piece) cleaned.rate_piece = parseFloat(v.rate_piece) || 0;
        if (typeConfig.rateOptions.includes('per kg') && v.rate_kg) cleaned.rate_kg = parseFloat(v.rate_kg) || 0;
        if (typeConfig.rateOptions.includes('per running feet') && v.rate_running_feet) cleaned.rate_running_feet = parseFloat(v.rate_running_feet) || 0;
        return cleaned;
      });

      const productData = {
        brand_name: form.hardware_type === 'lipping' ? '' : form.brand_name.trim().toUpperCase(),
        main_category: 'Screws/Patti',
        subcategory: form.subcategory,
        product_name: form.subcategory,
        hardware_type: form.hardware_type,
        hardware_variants: cleanedVariants,
        rate_type: typeConfig.rateOptions[0], // Primary rate type
        base_rate: 0,
        status: 'active',
        gst_included: true,
        show_brand_in_pdf: form.show_brand_in_pdf !== false,
      };

      if (editProduct) {
        await api.updateProduct(editProduct.id, productData);
        toast.success('Hardware product updated!');
      } else {
        await api.createProduct(productData);
        toast.success('Hardware product added!');
      }

      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const typeConfig = getCurrentTypeConfig();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hardware-form-dialog">
        <DialogHeader>
          <DialogTitle>{editProduct ? 'Edit' : 'Add'} Screws/Patti Product</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Hardware Type */}
          <div>
            <Label className="text-xs font-semibold">Hardware Type *</Label>
            <Select value={form.hardware_type} onValueChange={handleTypeChange}>
              <SelectTrigger className="mt-1" data-testid="hardware-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HARDWARE_TYPES.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#8F9485] mt-1">
              Size unit: {typeConfig.sizeUnit} | Rates: {typeConfig.rateOptions.join(', ')}
            </p>
          </div>

          {/* Brand Name (hidden for Lipping/Patti) */}
          {form.hardware_type !== 'lipping' && (
          <div className="relative">
            <Label className="text-xs font-semibold">Brand Name *</Label>
            <Input 
              value={form.brand_name} 
              onChange={e => handleBrandInput(e.target.value)}
              onBlur={() => setTimeout(() => setShowBrandDropdown(false), 200)}
              placeholder="Type brand name..."
              className="mt-1" 
              data-testid="hardware-brand-input"
            />
            {showBrandDropdown && brandSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-[#E2E6D9] rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                {brandSuggestions.map(b => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => selectBrand(b)}
                    className="w-full text-left px-3 py-2 hover:bg-[#F2F4EF] text-sm"
                  >
                    {b}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Subcategory/Type - Input with autocomplete */}
          <div className="relative">
            <Label className="text-xs font-semibold">Product Type *</Label>
            <Input 
              value={form.subcategory} 
              onChange={e => handleProductTypeInput(e.target.value)}
              onFocus={() => {
                // Show all suggestions on focus if empty
                if (!form.subcategory) {
                  setProductTypeSuggestions(getSubcategoryOptions());
                  setShowProductTypeDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowProductTypeDropdown(false), 200)}
              placeholder="Type or select product type..."
              className="mt-1" 
              data-testid="hardware-subcategory-input"
            />
            {showProductTypeDropdown && productTypeSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-[#E2E6D9] rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                {productTypeSuggestions.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => selectProductType(s)}
                    className="w-full text-left px-3 py-2 hover:bg-[#F2F4EF] text-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-[#8F9485] mt-1">
              Select from suggestions or type a custom product type
            </p>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Sizes & Rates</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addVariant}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Add Size
              </Button>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
              {form.hardware_variants.map((variant, index) => (
                <div 
                  key={index} 
                  className="p-3 bg-[#F9FAF7] rounded-lg border border-[#E2E6D9] space-y-2"
                >
                  <div className="flex items-center gap-2">
                    {/* Number field for Nails */}
                    {form.hardware_type === 'nail' && (
                      <div className="w-20">
                        <Label className="text-[10px] text-[#8F9485]">Number</Label>
                        <Input
                          value={variant.nail_number}
                          onChange={e => updateVariant(index, 'nail_number', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <Label className="text-[10px] text-[#8F9485]">Size ({typeConfig.sizeUnit})</Label>
                      <Input
                        value={variant.size}
                        onChange={e => updateVariant(index, 'size', e.target.value)}
                        placeholder=""
                        className="h-8 text-sm"
                      />
                    </div>
                    
                    {typeConfig.hasBoxQty && (
                      <div className="w-24">
                        <Label className="text-[10px] text-[#8F9485]">Pcs/Box</Label>
                        <Input
                          type="number"
                          value={variant.box_qty}
                          onChange={e => updateVariant(index, 'box_qty', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}

                    {form.hardware_variants.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVariant(index)}
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0 mt-4"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Rate Fields based on type */}
                  <div className="grid grid-cols-3 gap-2">
                    {typeConfig.rateOptions.includes('per box') && (
                      <div>
                        <Label className="text-[10px] text-[#8F9485]">Rate/Box (₹)</Label>
                        <Input
                          type="number"
                          value={variant.rate_box}
                          onChange={e => updateVariant(index, 'rate_box', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                    {typeConfig.rateOptions.includes('per dozen') && (
                      <div>
                        <Label className="text-[10px] text-[#8F9485]">Rate/Dozen (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={variant.rate_dozen}
                          onChange={e => updateVariant(index, 'rate_dozen', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                    {typeConfig.rateOptions.includes('per piece') && (
                      <div>
                        <Label className="text-[10px] text-[#8F9485]">Rate/Piece (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={variant.rate_piece}
                          onChange={e => updateVariant(index, 'rate_piece', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                    {typeConfig.rateOptions.includes('per kg') && (
                      <div>
                        <Label className="text-[10px] text-[#8F9485]">Rate/KG (₹)</Label>
                        <Input
                          type="number"
                          value={variant.rate_kg}
                          onChange={e => updateVariant(index, 'rate_kg', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                    {typeConfig.rateOptions.includes('per running feet') && (
                      <div>
                        <Label className="text-[10px] text-[#8F9485]">Rate/RFT (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={variant.rate_running_feet}
                          onChange={e => updateVariant(index, 'rate_running_feet', e.target.value)}
                          placeholder=""
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Per-product brand visibility in customer PDF */}
          {form.hardware_type !== 'lipping' && (
            <div className="flex items-start gap-2 p-3 rounded-md border border-[#E2E6D9] bg-[#F9FAF7]">
              <input
                id="hw-show-brand-in-pdf"
                type="checkbox"
                checked={form.show_brand_in_pdf !== false}
                onChange={(e) => setForm(f => ({ ...f, show_brand_in_pdf: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[#556B2F]"
                data-testid="form-show-brand-in-pdf"
              />
              <label htmlFor="hw-show-brand-in-pdf" className="text-xs leading-tight cursor-pointer select-none">
                <span className="font-semibold text-[#1A1C16]">Show brand name in customer PDF</span>
                <p className="text-[#5C6150] text-[10px] mt-0.5">
                  Turn off for generic items (e.g., zigzag pins, common screws). Keep on for branded items.
                </p>
              </label>
            </div>
          )}

          {/* Save Button */}
          <Button 
            onClick={handleSave} 
            disabled={saving} 
            className="w-full bg-[#556B2F] hover:bg-[#465926] text-white"
            data-testid="hardware-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
